import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useCallback, useState } from 'react';

import { localifyLink, useLocale } from '@/helpers/LangHelper';
import { useWindowSize } from '@/hooks/useWindowSize';
import { Article } from '@/types/Article';

import { LazyBackgroundImage } from '../lazy-background-image/lazy-background-image';
import styles from './article-card.module.scss';

export enum ArticleCardType {
  Small = 'small',
  Large = 'large',
}
interface Props {
  article: Article;
  type: ArticleCardType;
  fixedWidth?: boolean;
}

const ArticleCard: FC<Props> = (props) => {
  const { locale } = useLocale();

  const [isLoading] = useState<boolean>(false);
  const { article, type, fixedWidth } = props;

  const windowSize = useWindowSize();
  const getClassSkeletonImage = () => {
    if (isLoading) {
      return 'loading';
    } else {
      return '';
    }
  };

  const targetUrl = localifyLink(`/${locale}${article.articleUrl}`, locale);

  const getArticleImage = useCallback(() => {
    // Horiz image for small card
    let articleImage = article.leadImage || article.leadPortraitImage;
    if ((windowSize.width ?? 0) > 768 && type === ArticleCardType.Large) {
      // Portrait image for Large card
      articleImage = article.leadPortraitImage || article.leadImage;
    }
    return articleImage;
  }, [type, windowSize.width, article]);

  return (
    <div
      className={classNames(styles.articleCard, styles[type], { [styles.fixedWidth]: fixedWidth })}
      style={{ position: 'relative' }}
    >
      <div className={classNames(styles.articleCardImageWrapper, styles[getClassSkeletonImage()])}>
        <LazyBackgroundImage imageUrl={getArticleImage()} className={styles.articleCardImage} />
      </div>
      <div className={styles.articleCardContentWrapper}>
        {article.categories?.[0]?.name ? (
          <div className={styles.categoriesList}>
            <div>{article.categories?.[0]?.name}</div>
          </div>
        ) : null}
        <div className={styles.articleCardContent}>
          <div className={classNames(styles.articleCardContentTitle, styles.twoLinesLimit)}>
            {article.title}
          </div>
          <div className={classNames(styles.articleCardContentDescription, styles.twoLinesLimit)}>
            {`${dayjs(article.overrideDate || article.createdDate).format('MMM DD, YYYY')}`}

            {article.readingTime && (
              <span>
                &nbsp;&nbsp;&nbsp;<span className={styles.dot}>&#8226;</span>&nbsp;&nbsp;&nbsp;
                {`${article.readingTime} min read`}
              </span>
            )}
          </div>
        </div>
      </div>
      <a
        href={targetUrl}
        target={'_self'}
        className={styles.articleCardLink}
        title={article.title}
      />
    </div>
  );
};

export default ArticleCard;
